import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip, CircularProgress,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import { waveService } from '../../services/waveService';
import WmsStationHeader from '../../shared/WmsStationHeader';
import WmsListSection from '../../shared/WmsListSection';
import { WMS_TYPE_CHIP_SX } from '../../shared/wmsTypeChip';

const WMS_WAVE_STATUS_EVENT = 'wms-wave-status-changed';
const WMS_WAVE_STATUS_STORAGE_KEY = 'wms-wave-status-ts';

const isAdminWave = (w: { assignedPersonnelId?: string; assignedPickerName?: string }) =>
  !w.assignedPersonnelId && !w.assignedPickerName;

export interface PickingWaveSelectWave {
  id: string;
  waveNumber: string;
  waveType: string;
  marketplaceSource?: string;
  status: string;
  totalOrders: number;
  totalItems: number;
  pickedItems: number;
  assignedPersonnelId?: string;
  assignedPickerName?: string;
  cartCode?: string;
  startedAt?: string;
}

interface PickingWaveSelectProps {
  personnelName: string;
  personnelId?: string;
  warehouseId: string;
  onWaveSelected: (wave: PickingWaveSelectWave) => void;
  onBack?: () => void;
  channel?: string;
  channelLabel?: string;
  waveType?: 'Single' | 'Multi';
}

const PickingWaveSelect: React.FC<PickingWaveSelectProps> = ({
  personnelName, personnelId, warehouseId, onWaveSelected, onBack,
  channel, channelLabel, waveType,
}) => {
  const { t } = useTranslation('warehouse');
  const [waves, setWaves] = useState<PickingWaveSelectWave[]>([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const loadWaves = useCallback(async () => {
    if (!warehouseId) return;
    if (!hasLoadedRef.current) setLoading(true);
    try {
      const res = await waveService.getByWarehouse(warehouseId, {
        pageSize: 50,
        marketplaceSource: channel,
      });
      const all: PickingWaveSelectWave[] = res.data?.data?.items || res.data?.data || [];
      const visible = all.filter((w) => {
        const active = w.status === 'Created' || w.status === 'InProgress';
        if (!active) return false;
        if (waveType && w.waveType !== waveType) return false;
        if (isAdminWave(w)) return true;
        if (personnelId && w.assignedPersonnelId) return w.assignedPersonnelId === personnelId;
        return !!w.assignedPickerName && w.assignedPickerName === personnelName;
      });
      visible.sort((a, b) => Number(isAdminWave(b)) - Number(isAdminWave(a)));
      setWaves(visible);
    } catch { setWaves([]); }
    hasLoadedRef.current = true;
    setLoading(false);
  }, [warehouseId, personnelId, personnelName, channel, waveType]);

  useEffect(() => {
    loadWaves();
    const interval = setInterval(loadWaves, 5000);
    return () => clearInterval(interval);
  }, [loadWaves]);

  const handleStart = async (wave: PickingWaveSelectWave) => {
    setStarting(wave.id);
    try {
      if (wave.status === 'Created' && isAdminWave(wave)) {
        const claimId = personnelId || personnelName;
        if (!claimId) {
          alert(t('screens.picking.waveSelect.claimFailed'));
          setStarting(null);
          return;
        }
        await waveService.assignPersonnelToWave({
          WaveId: wave.id,
          PersonnelId: claimId,
          PersonnelName: personnelName,
        });
      }

      if (wave.status === 'Created') {
        await waveService.start(wave.id);
        window.dispatchEvent(new CustomEvent(WMS_WAVE_STATUS_EVENT, { detail: { warehouseId } }));
        localStorage.setItem(WMS_WAVE_STATUS_STORAGE_KEY, JSON.stringify({ ts: Date.now(), warehouseId }));
      }
      onWaveSelected({ ...wave, status: 'InProgress' });
    } catch (err: any) {
      const msg = err?.response?.data?.message
        || err?.response?.data?.Message
        || err?.message
        || t('screens.picking.waveSelect.startFailedDefault');
      alert(t('screens.picking.waveSelect.startFailedAlert', { msg }));
      loadWaves();
    }
    setStarting(null);
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 760, mx: 'auto' }}>
      {onBack && (
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ color: 'text.secondary', mb: 1 }}>
          {t('screens.picking.waveSelect.backToMenu')}
        </Button>
      )}
      <WmsStationHeader
        title={channelLabel
          ? t('screens.picking.waveSelect.channelTitle', {
              channel: channelLabel,
              type: waveType === 'Multi' ? t('screens.picking.common.multi') : t('screens.picking.common.single'),
            })
          : t('screens.picking.waveSelect.greeting', { name: personnelName })}
        subtitle={t('screens.picking.waveSelect.subtitleList')}
      />

      <WmsListSection
        title={t('screens.picking.common.workOrders')}
        count={waves.length}
        onRefresh={loadWaves}
        hasData={waves.length > 0}
        emptyText={t('screens.picking.waveSelect.emptyList')}
      >
        {loading && waves.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {waves.map(w => (
              <Card key={w.id} variant="outlined" sx={{
                bgcolor: 'background.paper',
                borderColor: w.status === 'InProgress' ? 'warning.main' : 'divider',
              }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {w.waveNumber}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      {isAdminWave(w) && (
                        <Chip size="small" label={t('screens.picking.waveSelect.adminWave')} color="primary" sx={WMS_TYPE_CHIP_SX} />
                      )}
                      <Chip size="small" label={w.waveType === 'Single' ? t('screens.picking.common.single') : t('screens.picking.common.multi')}
                        color={w.waveType === 'Single' ? 'info' : 'secondary'} sx={WMS_TYPE_CHIP_SX} />
                      <Chip size="small" label={t('screens.picking.common.orderCount', { count: w.totalOrders })} variant="outlined" />
                      <Chip size="small" label={t('screens.picking.common.itemsCount', { count: w.totalItems })} variant="outlined" />
                      {w.status === 'InProgress' && (
                        <Chip size="small" label={t('screens.picking.waveSelect.pickedProgress', { picked: w.pickedItems, total: w.totalItems })} color="warning" />
                      )}
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={starting === w.id ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                    disabled={starting !== null}
                    onClick={() => handleStart(w)}
                    sx={{ minWidth: 120, height: 48 }}
                  >
                    {w.status === 'InProgress' ? t('screens.picking.waveSelect.resume') : t('screens.picking.common.start')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </WmsListSection>
    </Box>
  );
};

export default PickingWaveSelect;
